import { Component, Input } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  CardModule,
  CheckboxModule,
  DropdownModule,
  RadioButtonModule,
  AlertModule,
  TooltipModule,
} from 'axis-components';
import { CommonModule, JsonPipe, SlicePipe } from '@angular/common';
import { BaseRules, Control, TextValues } from './hawaii-model';
import { UimuiBaseStateComponent } from '../uimui-base-state.component';
import { TruncateModule } from 'axis-components/truncate';

@Component({
  selector: 'app-hawaii',
  standalone: true,
  imports: [
    CardModule,
    CheckboxModule,
    CommonModule,
    DropdownModule,
    ReactiveFormsModule,
    RadioButtonModule,
    AlertModule,
    SlicePipe,
    TruncateModule,
    TooltipModule
  ],
  templateUrl: './hawaii.component.html',
  styleUrl: './hawaii.component.scss',
})
export class HawaiiComponent extends UimuiBaseStateComponent<
  BaseRules,
  TextValues
> {
  @Input() set formGroup(form: FormGroup | null) {
    if (form) {
      super.getJsonRuleFiles('hawaii', form);
    }
  }
  @Input() set isReview(review: boolean) {
    if (review) {
      super.getJsonRuleFiles('hawaii', null);
    }
  }

  /**
   * Evaluates liability dropdown premium when Hawaii stacked/non-stacked
   * radio selection changes under policy-liability flow.
   */
  override eventHook(value: any): void {
    if (this.baseRules?.selectionId !== 'PolicyLiabilityLimits_CombinedSingleLimit') {
      return;
    }

    const selectionKey = value?.value ?? value?.select ?? value;
    const liabilityControlName =
      selectionKey?.includes('UnderInsured') || selectionKey?.includes('UIM')
      ? 'UIM_LiabilityLimit_DropDown'
      : selectionKey?.includes('Uninsured')
        ? 'UM_LiabilityLimit_DropDown'
        : undefined;

    if (!liabilityControlName) {
      return;
    }

    const liabilityControl = this.controls?.find(
      (control) => control.formControlName === liabilityControlName,
    );
    const selectedOption = liabilityControl
      ? this.getSelectedDropdownOption(liabilityControl)
      : undefined;

    if (liabilityControl && selectedOption) {
      this.handleDropdownSelection(liabilityControl, selectedOption);
    }
  }

  
  private getSelectedDropdownOption(control: Control): any {
    const selectedValue = this.reactiveForm.get(control.formControlName)?.value;

    if (selectedValue && typeof selectedValue === 'object') {
      return selectedValue;
    }

    return (
      control.data?.find(
        (item: any) =>
          item?.id === selectedValue || item?.name === selectedValue,
      ) ?? control.data?.[0]
    );
  }

  /**
   * Hawaii dropdown data carries `stackedPremium` and `nonStackedPremium`
   * instead of a single `premium` field. This maps to the right CSL lookup
   * control when policy-liability based premium calculation is needed.
   */
  protected override getLookupControlName(control: Control): string | undefined {
    const controlName = control.formControlName;

    return controlName.includes('UnderInsured') || controlName.includes('UIM')
      ? 'CombinedSingleLimit_UnderInsured_DropDown'
      : 'CombinedSingleLimit_Uninsured_DropDown';
  }

  /**
   * Returns the premium key to read from Hawaii dropdown options based on
   * the current stacked/non-stacked radio selection for UIM or UM.
   */
  protected override getSelectedPremiumKey(control: Control): string | undefined {
    const isUIM =
      control.formControlName.includes('UnderInsured') ||
      control.formControlName.includes('UIM');
    const selectionControlName = isUIM
      ? 'levelOne_UnderInsured_Radio'
      : 'levelOne_Uninsured_Radio';
    const selection = this.reactiveForm.get(selectionControlName)?.value;
    const selectionKey = selection?.value ?? selection?.select ?? selection;

    if (
      selectionKey === 'SelectStacked_LevelOne_UnderInsured_Radio' ||
      selectionKey === 'SelectStacked_LevelOne_Uninsured_Radio'
    ) {
      return 'stackedPremium';
    }

    if (
      selectionKey === 'SelectNonStacked_LevelOne_UnderInsured_Radio' ||
      selectionKey === 'SelectNonStacked_LevelOne_Uninsured_Radio'
    ) {
      return 'nonStackedPremium';
    }

    return undefined;
  }

  protected override getItemPremium(item: any, control?: Control): string {
    const premiumKey = control ? this.getSelectedPremiumKey(control) : undefined;
    if (premiumKey) {
      return item?.[premiumKey] ?? '';
    }

    return item?.premium ?? '';
  }

  protected override getPdfPremiumKey(control: Control): string {
    const isUIM =
      control.formControlName.includes('UnderInsured') ||
      control.formControlName.includes('UIM');

    if (this.getSelectedPremiumKey(control) === 'nonStackedPremium') {
      return isUIM
        ? 'nonStackedLiabilityPremiumUIM'
        : 'nonStackedLiabilityPremiumUM';
    }

    return isUIM ? 'stackedLiabilityPremiumUIM' : 'stackedLiabilityPremiumUM';
  }
}
